/**
 * Context-pad actions. Teams, interactions and flows: rename, add an attached
 * annotation, delete. Annotations: edit text, connect to an element, delete. An
 * annotation connector: delete. A multi-selection: delete all.
 *
 * Entries are focusable buttons; Enter or Space runs their keyboard action.
 * Connecting has none, because it is a pointer gesture that needs a drop target,
 * so Tab skips it. "Add annotation" on the element attaches one by keyboard.
 *
 * The canvas keys act only while the canvas has the focus. Entries that open the
 * label editor leave the focus to it; connect and delete hand it to the canvas,
 * which a pointer press would otherwise leave on the entry or, once the entry
 * goes, on `<body>`. diagram-js rebuilds the pad whenever its element changes,
 * e.g. on an undo, and closes it when the element goes or the lock comes on: a
 * focused entry then moves to the same entry of the new pad, or to the canvas.
 *
 * Every entry changes the diagram, so under the read-only lock there is no context pad at all.
 */

import type Canvas from "diagram-js/lib/core/Canvas";
import type EventBus from "diagram-js/lib/core/EventBus";
import type ContextPad from "diagram-js/lib/features/context-pad/ContextPad";
import type Modeling from "diagram-js/lib/features/modeling/Modeling";
import type Connect from "diagram-js/lib/features/connect/Connect";
import type Selection from "diagram-js/lib/features/selection/Selection";
import type {
  ContextPadEntries,
  ContextPadEntry,
  default as ContextPadProvider,
} from "diagram-js/lib/features/context-pad/ContextPadProvider";
import type { Element } from "diagram-js/lib/model/Types";
import {
  isTtAnnotation,
  isTtAssociation,
  isTtElement,
  isTtFlow,
  isTtInteraction,
  type TtElement,
} from "../model/di-types.js";
import type TtLabelEditing from "../label-editing/TtLabelEditing.js";
import type TtModeling from "../modeling/TtModeling.js";
import { READ_ONLY_CHANGED_EVENT, type TtReadOnly } from "../read-only/index.js";
import { handFocusToCanvas, watchDroppedFocus } from "../keyboard/canvasFocus.js";
import { isKeyOnHiddenTarget } from "../keyboard/TtKeyboard.js";
import {
  ICON_LINK,
  ICON_MESSAGE_SQUARE_PLUS,
  ICON_PENCIL,
  ICON_TRASH,
  iconMarkup,
  type IconNode,
} from "../draw/icons.js";

const ENTRY_SELECTOR = ".djs-context-pad .entry[data-action]";
const PAD_PARENT_SELECTOR = ":scope > .djs-context-pad-parent";

function cpHtml(icon: IconNode, title: string, focusable = true): string {
  return `<div class="entry tt-cp-entry" role="button" tabindex="${focusable ? 0 : -1}" aria-label="${title}">${iconMarkup(icon)}</div>`;
}

function editTitle(element: TtElement): string {
  if (isTtAnnotation(element)) return "Edit text";
  if (isTtInteraction(element) || isTtFlow(element)) return "Edit label";
  return "Rename";
}

export default class TtContextPadProvider implements ContextPadProvider {
  static $inject = [
    "contextPad",
    "modeling",
    "ttModeling",
    "ttLabelEditing",
    "connect",
    "selection",
    "canvas",
    "eventBus",
    "injector",
  ];

  private readonly readOnly: TtReadOnly | null;

  constructor(
    private readonly contextPad: ContextPad,
    private readonly modeling: Modeling,
    private readonly ttModeling: TtModeling,
    private readonly labelEditing: TtLabelEditing,
    private readonly connect: Connect,
    private readonly selection: Selection,
    private readonly canvas: Canvas,
    eventBus: EventBus,
    injector: { get<T>(name: string, strict?: boolean): T | null },
  ) {
    this.readOnly = injector.get<TtReadOnly>("ttReadOnly", false);
    contextPad.registerProvider(this);
    eventBus.on("contextPad.open.allowed", () => (this.readOnly?.isReadOnly() ? false : undefined));
    eventBus.on(READ_ONLY_CHANGED_EVENT, (event: { readOnly?: boolean }) => {
      if (event.readOnly) contextPad.close();
    });
    const container = canvas.getContainer();
    container.addEventListener("keydown", (keyboardEvent) => this.onEntryKeyDown(keyboardEvent));
    const padParent = container.querySelector<HTMLElement>(PAD_PARENT_SELECTOR);
    if (padParent) {
      const stopWatching = watchDroppedFocus(padParent, (lost) =>
        this.focusSameEntryOrCanvas(lost.closest(ENTRY_SELECTOR)?.getAttribute("data-action")),
      );
      eventBus.on("diagram.destroy", stopWatching);
    }
  }

  getContextPadEntries(element: Element): ContextPadEntries {
    if (this.readOnly?.isReadOnly()) return {};
    if (isTtAssociation(element))
      return { delete: this.deleteEntry([element], "Delete connector") };
    if (!isTtElement(element)) return {};

    if (isTtAnnotation(element)) {
      const startConnect = (event: Event) => {
        this.connect.start(event as MouseEvent, element);
        handFocusToCanvas(this.canvas, event);
      };
      return {
        "edit-label": this.editEntry(element),
        connect: {
          group: "edit",
          title: "Attach to element",
          html: cpHtml(ICON_LINK, "Attach to element", false),
          action: { click: startConnect },
        },
        delete: this.deleteEntry([element], "Delete"),
      };
    }

    const appendAnnotation = () => {
      const annotation = this.ttModeling.appendAnnotation(element);
      this.selection.select(annotation as unknown as Element);
      this.labelEditing.activate(annotation);
    };
    return {
      "edit-label": this.editEntry(element),
      "append-annotation": {
        group: "edit",
        title: "Add annotation",
        html: cpHtml(ICON_MESSAGE_SQUARE_PLUS, "Add annotation"),
        action: { click: appendAnnotation, keyboard: appendAnnotation },
      },
      delete: this.deleteEntry([element], "Delete"),
    };
  }

  getMultiElementContextPadEntries(elements: Element[]): ContextPadEntries {
    if (this.readOnly?.isReadOnly()) return {};
    return { delete: this.deleteEntry(elements, `Delete ${elements.length} elements`) };
  }

  private onEntryKeyDown(event: KeyboardEvent): void {
    if ((event.key !== "Enter" && event.key !== " ") || event.repeat || isKeyOnHiddenTarget(event))
      return;
    const entry = (event.target as HTMLElement | null)?.closest?.(ENTRY_SELECTOR);
    const action = entry?.getAttribute("data-action");
    if (!action) return;
    event.preventDefault();
    event.stopPropagation();
    this.contextPad.triggerEntry(action, "keyboard", event);
  }

  private focusSameEntryOrCanvas(action: string | null | undefined): void {
    const container = this.canvas.getContainer();
    const entry = action
      ? container.querySelector<HTMLElement>(
          `.djs-context-pad.open .entry[data-action="${CSS.escape(action)}"]`,
        )
      : null;
    entry?.focus({ preventScroll: true });
    if (container.ownerDocument.activeElement !== entry) this.canvas.focus();
  }

  private editEntry(element: TtElement): ContextPadEntry {
    const title = editTitle(element);
    const activate = () => this.labelEditing.activate(element);
    return {
      group: "edit",
      title,
      html: cpHtml(ICON_PENCIL, title),
      action: { click: activate, keyboard: activate },
    };
  }

  private deleteEntry(elements: Element[], title: string): ContextPadEntry {
    const remove = () => {
      this.modeling.removeElements([...elements]);
      this.canvas.focus();
    };
    return {
      group: "edit",
      title,
      html: cpHtml(ICON_TRASH, title),
      action: { click: remove, keyboard: remove },
    };
  }
}
